import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Card } from 'primereact/card';
import { FileUpload } from 'primereact/fileupload';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Slider } from 'primereact/slider';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import Draggable from 'react-draggable';
import { documentService } from '../services/documentService';
import AdvancedColorPicker from '../components/AdvancedColorPicker';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.mjs';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.mjs',
  import.meta.url
).toString();

const DEFAULT_DOC_WIDTH = 595.44;
const DEFAULT_DOC_HEIGHT = 841.68;

const DocumentUploadPage = () => {
  const navigate = useNavigate();
  const toast = useRef(null);
  const containerRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    qrCodeColor: '#41A6D7',
    qrCodeSize: 80,
    qrMargin: 0,
    qrPosition: { x: DEFAULT_DOC_WIDTH / 2, y: DEFAULT_DOC_HEIGHT / 2 }, // center
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [docDimensions, setDocDimensions] = useState({
    width: DEFAULT_DOC_WIDTH,
    height: DEFAULT_DOC_HEIGHT,
  });
  const [previewDimensions, setPreviewDimensions] = useState({ width: 0, height: 0 });
  const [qrPreviewPosition, setQrPreviewPosition] = useState({ x: 0, y: 0 });

  const uploadMutation = useMutation({
    mutationFn: documentService.uploadDocument,
    onSuccess: () => {
      toast.current.show({ severity: 'success', summary: 'Uploaded', detail: 'Document uploaded successfully!' });
      navigate('/documents');
    },
    onError: (error) => {
      toast.current.show({ severity: 'error', summary: 'Error', detail: error.message });
    },
  });

  // Convert preview top-left to document center coordinates
  const convertPreviewToDoc = useCallback(
    (previewPos) => {
      if (!previewDimensions.width || !previewDimensions.height) return { x: 0, y: 0 };
      const scaleX = docDimensions.width / previewDimensions.width;
      const scaleY = docDimensions.height / previewDimensions.height;

      return {
        x: previewPos.x * scaleX + formData.qrCodeSize / 2,
        y: docDimensions.height - (previewPos.y * scaleY + formData.qrCodeSize / 2), // flip Y-axis
      };
    },
    [previewDimensions, docDimensions, formData.qrCodeSize]
  );

  // Convert document center to preview top-left for Draggable
  const convertDocToPreview = useCallback(
    (docPos) => {
      if (!previewDimensions.width || !previewDimensions.height) return { x: 0, y: 0 };
      const scaleX = previewDimensions.width / docDimensions.width;
      const scaleY = previewDimensions.height / docDimensions.height;

      return {
        x: (docPos.x - formData.qrCodeSize / 2) * scaleX,
        y: (docDimensions.height - docPos.y - formData.qrCodeSize / 2) * scaleY, // flip Y-axis
      };
    },
    [previewDimensions, docDimensions, formData.qrCodeSize]
  );

  // Handle QR drag stop
  const handleQrDragStop = (e, data) => {
    const docPos = convertPreviewToDoc({ x: data.x, y: data.y });
    setFormData((p) => ({ ...p, qrPosition: docPos }));
  };

  // Update preview QR position whenever doc or QR data changes
  useEffect(() => {
    if (!docDimensions.width || !previewDimensions.width) return;
    const updatedPreviewPos = convertDocToPreview(formData.qrPosition);
    setQrPreviewPosition(updatedPreviewPos);
  }, [docDimensions, previewDimensions, formData.qrPosition, convertDocToPreview]);

  // File selection and preview generation
  const handleFileSelect = async (e) => {
    const file = e.files[0];
    if (!file) return;

    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.current.show({ severity: 'error', summary: 'Invalid file type' });
      return;
    }

    setSelectedFile(file);

    if (file.type === 'application/pdf') {
      const pdfData = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
      const page = await pdf.getPage(1);
      const viewport1 = page.getViewport({ scale: 1 });
      setDocDimensions({ width: viewport1.width, height: viewport1.height });

      const maxPreviewWidth = 600;
      const scale = Math.min(maxPreviewWidth / viewport1.width, 1.5);
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext('2d');
      await page.render({ canvasContext: ctx, viewport }).promise;
      setPreviewUrl(canvas.toDataURL());
      setPreviewDimensions({ width: canvas.width, height: canvas.height });
    } else {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        setDocDimensions({ width: img.naturalWidth, height: img.naturalHeight });
        setPreviewDimensions({ width: img.width, height: img.height });
        setPreviewUrl(img.src);
      };
    }

    if (!formData.title.trim()) {
      setFormData((p) => ({ ...p, title: file.name.replace(/\.[^/.]+$/, '') }));
    }

    // Place QR in center
    setFormData((p) => ({
      ...p,
      qrPosition: { x: docDimensions.width / 2, y: docDimensions.height / 2 },
    }));
  };

  const handleInputChange = (field, value) =>
    setFormData((p) => ({ ...p, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.current.show({ severity: 'error', summary: 'Select a file' });
      return;
    }

    setIsUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('document', selectedFile);
      uploadFormData.append('title', formData.title);
      uploadFormData.append('description', formData.description);
      uploadFormData.append('qrCodeColor', formData.qrCodeColor);
      uploadFormData.append('qrCodeSize', formData.qrCodeSize);
      uploadFormData.append('qrMargin', formData.qrMargin);
      uploadFormData.append('qrPosition', JSON.stringify(formData.qrPosition));

      await uploadMutation.mutateAsync(uploadFormData);
    } catch (err) {
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  // useEffect(() => {
  //   console.log("FORMDATA", formData);
  //   console.log("DOC DIMENSIONS", docDimensions);
  //   console.log("PREVIEW DIMENSIONS", previewDimensions);
  //   console.log("QR PREVIEW POSITION", qrPreviewPosition);
  // }, [formData]);

  return (
    <div className="grid">
      <div className="col-12">
        <Card>
          <Toast ref={toast} />
          <div className="flex justify-content-between align-items-center mb-3">
            <h2>Upload Document</h2>
            <Button
              label="Back"
              icon="pi pi-arrow-left"
              className="p-button-text"
              onClick={() => navigate('/documents')}
            />
          </div>

          <form onSubmit={handleSubmit} className="flex flex-column gap-4">
            <FileUpload
              mode="basic"
              name="document"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              customUpload
              uploadHandler={handleFileSelect}
              chooseLabel="Select File"
              disabled={isUploading}
              className="fileupload-custom"
            />

            {previewUrl && (
              <div
                ref={containerRef}
                className="relative border-1 surface-border border-round overflow-hidden"
                style={{ width: '100%', maxWidth: 600, margin: '0 auto', position: 'relative' }}
              >
                <img
                  src={previewUrl}
                  alt="Document preview"
                  style={{ width: '100%', display: 'block', position: 'relative', zIndex: 1 }}
                  onLoad={(e) => {
                    const { clientWidth, clientHeight } = e.target;
                    setPreviewDimensions({ width: clientWidth, height: clientHeight });
                  }}
                />

                <Draggable
                  bounds="parent"
                  onStop={handleQrDragStop}
                  position={qrPreviewPosition}
                  onDrag={(e, data) => setQrPreviewPosition({ x: data.x, y: data.y })}
                >
                  <div
                    style={{
                      width: `${formData.qrCodeSize}px`,
                      height: `${formData.qrCodeSize}px`,
                      backgroundColor: formData.qrCodeColor,
                      opacity: 0.35,
                      border: '2px dashed rgba(0,0,0,0.5)',
                      borderRadius: 8,
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      cursor: 'move',
                      zIndex: 10,
                    }}
                    title="QR placement preview"
                  />
                </Draggable>
              </div>
            )}

            <div className="grid mt-4" style={{ backgroundColor: '#efefef', padding: '1rem', borderRadius: '8px' }}>
              <div className="col-12 md:col-6">
                <label className="form-label">QR Size (px)</label>
                <Slider
                  value={formData.qrCodeSize}
                  onChange={(e) => handleInputChange('qrCodeSize', e.value)}
                  min={50}
                  max={300}
                />
                <span>{formData.qrCodeSize}px</span>
              </div>
              <div className="col-12 md:col-6">
                <label className="form-label">QR Margin (px)</label>
                <Slider
                  value={formData.qrMargin}
                  onChange={(e) => handleInputChange('qrMargin', e.value)}
                  min={0}
                  max={10}
                />
                <span>{formData.qrMargin}px</span>
              </div>
            </div>

            <div>
              <label className="form-label">QR Color</label>
              <AdvancedColorPicker
                value={formData.qrCodeColor}
                onChange={(val) => handleInputChange('qrCodeColor', val)}
              />
            </div>

            <div>
              <label className="form-label">Title *</label>
              <InputText
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="w-full"
              />
            </div>

            <div>
              <label className="form-label">Description</label>
              <InputTextarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full"
              />
            </div>

            <div className="flex justify-content-end gap-2">
              <Button
                label="Cancel"
                icon="pi pi-times"
                className="p-button-text"
                onClick={() => navigate('/documents')}
                disabled={isUploading}
              />
              <Button
                type="submit"
                label="Upload"
                icon="pi pi-upload"
                loading={isUploading}
                disabled={isUploading}
                outlined
              />
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default DocumentUploadPage;
